import { type AxiosRequestConfig, type AxiosResponse } from "axios";

import { packNetAxios } from "@/lib/api-client";

type CallbackFunction<T> = (data: T) => void;
type ErrorCallbackFunction = (error: unknown) => void;

export type UseAxiosReturn<T = unknown> = {
	get: (setCallBack?: CallbackFunction<T>, errorCallBack?: ErrorCallbackFunction) => Promise<T | undefined>;
	getWithUrl: (
		urlArgs: string,
		setCallBack?: CallbackFunction<T>,
		errorCallBack?: ErrorCallbackFunction,
	) => Promise<AxiosResponse<T> | undefined>;
	addWithUrl: (
		urlArgs: string,
		data: unknown,
		setCallBack?: CallbackFunction<T>,
		errorCallBack?: ErrorCallbackFunction,
		options?: AxiosRequestConfig,
	) => Promise<void>;
};

/**
 * @deprecated Please put api-wrappers in src/api so we can help each other out and re use clients.
 */
export default function useAxios<T = unknown>(url: string): UseAxiosReturn<T> {
	const get = async (
		setCallBack?: CallbackFunction<T>,
		errorCallBack?: ErrorCallbackFunction,
	): Promise<T | undefined> => {
		try {
			const response = await packNetAxios.get<T>(url);
			if (setCallBack) setCallBack(response.data);
			return response.data;
		} catch (err) {
			if (errorCallBack) {
				errorCallBack(err);
				return undefined;
			} else {
				throw err;
			}
		}
	};

	const getWithUrl = async (
		urlArgs: string,
		setCallBack?: CallbackFunction<T>,
		errorCallBack?: ErrorCallbackFunction,
	): Promise<AxiosResponse<T> | undefined> => {
		try {
			const response = await packNetAxios.get<T>(`${url}/${urlArgs}`);
			if (setCallBack) setCallBack(response.data);
			return response;
		} catch (err) {
			if (errorCallBack) {
				errorCallBack(err);
				return undefined;
			} else {
				throw err;
			}
		}
	};

	const addWithUrl = async (
		urlArgs: string,
		data: unknown,
		setCallBack?: CallbackFunction<T>,
		errorCallBack?: ErrorCallbackFunction,
		options?: AxiosRequestConfig,
	): Promise<void> => {
		try {
			const response = await packNetAxios.post<T>(`${url}/${urlArgs}`, data, options);
			if (setCallBack) setCallBack(response.data);
		} catch (err) {
			if (errorCallBack) errorCallBack(err);
			else throw err;
		}
	};

	return {
		get,
		getWithUrl,
		addWithUrl,
	};
}
